import { useState, type FormEvent } from 'react'
import { Vacio } from '../componentes/Estados.js'
import { Icono } from '../componentes/Icono.js'
import {
  AvisoCorreccion,
  CabezaLibreta,
  Cuaderno,
  Lado,
  puntosDe,
  Salida,
  Teclado,
} from '../componentes/Libreta.js'
import type { ManoAnotada } from '../componentes/Libreta.js'
import type { Jugador } from '../datos/index.js'
import {
  claveDe,
  conManos,
  contar,
  nombresUsados,
  OBJETIVO_POR_DEFECTO,
  OBJETIVOS,
  PERIODOS,
  ranking,
  sillasDe,
  terminadasEn,
  useLibretaPersonal,
} from '../datos/libreta-personal.js'
import type { FilaRanking, PartidaPersonal, Periodo } from '../datos/libreta-personal.js'
import { conSigno, signoDe } from '../formato.js'
import { ir, reemplazar, volver } from '../navegacion.js'

/**
 * La libreta personal: las partidas que el jugador anota por su cuenta.
 *
 * Tiene otro color a propósito. Todo lo demás de la web es de los torneos, con
 * su violeta, y esto no: son las partidas del café, que no valida nadie y no
 * suman en ninguna tabla oficial. El verde y el ámbar lo dicen antes de leer
 * nada, y la pastilla «Fuera de torneo» lo dice con palabras.
 *
 * La anotación es la misma libreta del torneo (las piezas salen de
 * `componentes/Libreta.tsx`): el que aprendió a anotar una mesa ya sabe usar
 * esta. Lo que cambia es dónde se guarda, que es el propio teléfono (ver
 * `datos/libreta-personal.ts`).
 */

/** Cómo se llama el dueño en su propia libreta. */
function miNombre(jugador: Jugador): string {
  return jugador.apodo ?? jugador.nombre.split(' ')[0] ?? jugador.nombre
}

function pila(nombre: string): string {
  return nombre.trim().split(' ')[0] ?? nombre
}

/** «Hoy 18:40», «Ayer 21:05», «12 oct · 18:40». */
function cuandoFue(iso: string, ahora = new Date()): string {
  const d = new Date(iso)
  const hora = d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const dias = Math.round((dia(ahora) - dia(d)) / 86_400_000)
  if (dias === 0) return `Hoy ${hora}`
  if (dias === 1) return `Ayer ${hora}`
  const fecha = d.toLocaleDateString('es', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== ahora.getFullYear() ? { year: 'numeric' } : {}),
  })
  return `${fecha} · ${hora}`
}

function Cabecera({ titulo, children }: { titulo: string; children?: React.ReactNode }) {
  return (
    <header className="libre-cabeza">
      <span className="libre-pastilla">
        <Icono nombre="libreta" tamano={14} />
        Fuera de torneo
      </span>
      <h1>{titulo}</h1>
      {children}
    </header>
  )
}

/* ── La portada: ranking e historial ──────────────────────────────────── */

export function LibretaPersonal({ jugador }: { jugador: Jugador }) {
  const { partidas } = useLibretaPersonal(jugador.id)
  const [periodo, setPeriodo] = useState<Periodo>('semana')
  const ahora = new Date()
  const yo = miNombre(jugador)

  const enJuego = partidas.filter((p) => p.terminada === null)
  const delPeriodo = terminadasEn(partidas, periodo, ahora)
  const filas = ranking(delPeriodo, yo)
  const mia = filas.find((f) => f.esYo) ?? null
  const puesto = mia ? filas.indexOf(mia) + 1 : null

  return (
    <div className="pantalla libre">
      <Cabecera titulo="Mi libreta">
        <p className="tenue">
          Tus partidas sueltas: las del café, la casa o el club. No cuentan para ningún torneo y
          se guardan solo en este teléfono.
        </p>
      </Cabecera>

      <button className="boton principal libre-nueva" onClick={() => ir({ nombre: 'libreta-nueva' })}>
        <Icono nombre="mas" tamano={18} />
        Nueva partida
      </button>

      {partidas.length === 0 ? (
        <Vacio titulo="Tu libreta está vacía">
          <p>
            Anotá tu primera partida con la misma libreta de los torneos. Acá vas a ver tu ranking
            del día, la semana y el mes contra la gente con la que jugás.
          </p>
        </Vacio>
      ) : (
        <>
          {enJuego.length > 0 && (
            <section className="seccion">
              <h2 className="seccion-titulo">En juego</h2>
              <ul className="libre-lista">
                {enJuego.map((p) => (
                  <FilaPartida key={p.id} partida={p} yo={yo} ahora={ahora} />
                ))}
              </ul>
            </section>
          )}

          <section className="seccion">
            <h2 className="seccion-titulo">Ranking</h2>
            <nav className="segmentos" aria-label="Período del ranking">
              {PERIODOS.map((p) => (
                <button
                  key={p.id}
                  className={periodo === p.id ? 'activo' : ''}
                  aria-pressed={periodo === p.id}
                  onClick={() => setPeriodo(p.id)}
                >
                  {p.texto}
                </button>
              ))}
            </nav>

            {mia ? (
              <>
                <MiResumen fila={mia} puesto={puesto ?? 1} de={filas.length} />
                <TablaRanking filas={filas} />
              </>
            ) : (
              <p className="libre-nada">
                {periodo === 'hoy'
                  ? 'Hoy todavía no terminaste ninguna partida.'
                  : 'No terminaste ninguna partida en este período.'}
              </p>
            )}
          </section>

          {delPeriodo.length > 0 && (
            <section className="seccion">
              <h2 className="seccion-titulo">Partidas</h2>
              <ul className="libre-lista">
                {delPeriodo.map((p) => (
                  <FilaPartida key={p.id} partida={p} yo={yo} ahora={ahora} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function MiResumen({ fila, puesto, de }: { fila: FilaRanking; puesto: number; de: number }) {
  const diferencia = fila.aFavor - fila.enContra
  return (
    <div className="libre-resumen">
      <p className="libre-puesto">
        {de === 1 ? (
          'Sos el único en el ranking'
        ) : (
          <>
            Vas <b>{puesto}º</b> de {de}
          </>
        )}
      </p>
      <dl className="libre-cifras">
        <div>
          <dt>Jugadas</dt>
          <dd>{fila.jugadas}</dd>
        </div>
        <div>
          <dt>Ganadas</dt>
          <dd>{fila.ganadas}</dd>
        </div>
        <div>
          <dt>Efectividad</dt>
          <dd>{fila.porcentaje}%</dd>
        </div>
        <div>
          <dt>Puntos</dt>
          <dd className={signoDe(diferencia)}>{conSigno(diferencia)}</dd>
        </div>
      </dl>
    </div>
  )
}

function TablaRanking({ filas }: { filas: FilaRanking[] }) {
  return (
    <table className="tabla libre-tabla">
      <thead>
        <tr>
          <th className="col-puesto">#</th>
          <th>Jugador</th>
          <th className="num" title="Partidas jugadas">
            PJ
          </th>
          <th className="num" title="Partidas ganadas">
            PG
          </th>
          <th className="num" title="Ganadas sobre jugadas">
            %
          </th>
          <th className="num" title="Puntos a favor menos en contra">
            Dif
          </th>
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => {
          const diferencia = f.aFavor - f.enContra
          return (
            <tr key={f.clave} className={f.esYo ? 'mia' : ''}>
              <td className="col-puesto num">{i + 1}</td>
              <td>{f.esYo ? `${f.nombre} (vos)` : f.nombre}</td>
              <td className="num">{f.jugadas}</td>
              <td className="num">{f.ganadas}</td>
              <td className="num">{f.porcentaje}</td>
              <td className={`num ${f.esYo ? '' : signoDe(diferencia)}`}>{conSigno(diferencia)}</td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function FilaPartida({ partida, yo, ahora }: { partida: PartidaPersonal; yo: string; ahora: Date }) {
  const cuenta = contar(partida)
  const resultado =
    cuenta.ganador === null ? 'en-juego' : cuenta.ganador === 1 ? 'ganada' : 'perdida'
  return (
    <li>
      <button
        className={`libre-partida ${resultado}`}
        onClick={() => ir({ nombre: 'libreta-partida', id: partida.id })}
      >
        <span className="libre-partida-texto">
          <span className="libre-partida-quienes">
            {pila(yo)} y {pila(partida.companero)} <i>vs</i> {pila(partida.rivales[0])} y{' '}
            {pila(partida.rivales[1])}
          </span>
          <span className="tenue">
            {cuandoFue(partida.terminada ?? partida.creada, ahora)} · a {partida.objetivo}
          </span>
        </span>
        <span className="libre-partida-marcador">
          <b>
            {cuenta.bando1}-{cuenta.bando2}
          </b>
          <small>
            {resultado === 'en-juego' ? 'en juego' : resultado === 'ganada' ? 'ganada' : 'perdida'}
          </small>
        </span>
        <Icono nombre="flecha" tamano={18} />
      </button>
    </li>
  )
}

/* ── Empezar una partida ──────────────────────────────────────────────── */

export function NuevaPartida({ jugador }: { jugador: Jugador }) {
  const { partidas, crear } = useLibretaPersonal(jugador.id)
  const yo = miNombre(jugador)
  const sugeridos = nombresUsados(partidas)

  /* La última partida trae los mismos de siempre: casi siempre se juega con la
     misma gente, y escribir tres nombres en cada revancha cansa. */
  const ultima = partidas[0]
  const [companero, setCompanero] = useState(ultima?.companero ?? '')
  const [rival1, setRival1] = useState(ultima?.rivales[0] ?? '')
  const [rival2, setRival2] = useState(ultima?.rivales[1] ?? '')
  const [objetivo, setObjetivo] = useState<number>(ultima?.objetivo ?? OBJETIVO_POR_DEFECTO)
  const [aviso, setAviso] = useState<string | null>(null)

  function empezar(e: FormEvent) {
    e.preventDefault()
    const nombres = [companero, rival1, rival2].map((n) => n.trim().replace(/\s+/g, ' '))
    if (nombres.some((n) => n === '')) {
      setAviso('Escribí el nombre de los tres: tu compañero y los dos rivales.')
      return
    }
    const claves = [claveDe(yo), ...nombres.map(claveDe)]
    if (new Set(claves).size !== claves.length) {
      setAviso('Hay un nombre repetido. Cada uno de los cuatro tiene que tener el suyo.')
      return
    }
    const [c, r1, r2] = nombres as [string, string, string]
    const partida = crear({ companero: c, rivales: [r1, r2], objetivo })
    reemplazar({ nombre: 'libreta-partida', id: partida.id })
  }

  return (
    <div className="pantalla libre libreta-pantalla">
      <button className="libreta-volver" onClick={() => volver({ nombre: 'libreta' })}>
        <Icono nombre="atras" tamano={20} />
        Volver a mi libreta
      </button>

      <Cabecera titulo="Nueva partida" />

      <form className="libre-form" onSubmit={empezar}>
        <datalist id="libre-nombres">
          {sugeridos.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>

        <fieldset className="libre-equipo nosotros">
          <legend>Nosotros</legend>
          <p className="libre-yo">{yo}</p>
          <input
            value={companero}
            onChange={(e) => setCompanero(e.target.value)}
            placeholder="Tu compañero"
            aria-label="Tu compañero"
            list="libre-nombres"
            autoComplete="off"
          />
        </fieldset>

        <fieldset className="libre-equipo ellos">
          <legend>Ellos</legend>
          <input
            value={rival1}
            onChange={(e) => setRival1(e.target.value)}
            placeholder="Rival a tu derecha"
            aria-label="Primer rival, el que se sienta a tu derecha"
            list="libre-nombres"
            autoComplete="off"
          />
          <input
            value={rival2}
            onChange={(e) => setRival2(e.target.value)}
            placeholder="Su compañero"
            aria-label="Segundo rival"
            list="libre-nombres"
            autoComplete="off"
          />
        </fieldset>

        <div className="libre-objetivo">
          <span className="seccion-titulo">Se juega a</span>
          <div className="segmentos" role="group" aria-label="Puntos para ganar">
            {OBJETIVOS.map((o) => (
              <button
                key={o}
                type="button"
                className={objetivo === o ? 'activo' : ''}
                aria-pressed={objetivo === o}
                onClick={() => setObjetivo(o)}
              >
                {o}
              </button>
            ))}
          </div>
        </div>

        {aviso && (
          <p className="planilla-aviso" role="alert">
            {aviso}
          </p>
        )}

        <button className="boton principal" type="submit">
          Abrir la libreta
        </button>
      </form>
    </div>
  )
}

/* ── Anotar una partida ───────────────────────────────────────────────── */

export function PartidaSuelta({ jugador, partidaId }: { jugador: Jugador; partidaId: string }) {
  const { partidas, guardar, borrar, crear } = useLibretaPersonal(jugador.id)
  const partida = partidas.find((p) => p.id === partidaId) ?? null
  const alVolver = () => volver({ nombre: 'libreta' })

  return (
    <div className="pantalla libre libreta-pantalla">
      {!partida && (
        <button className="libreta-volver" onClick={alVolver}>
          <Icono nombre="atras" tamano={20} />
          Volver a mi libreta
        </button>
      )}

      {partida ? (
        <LibretaSuelta
          partida={partida}
          alVolver={alVolver}
          yo={miNombre(jugador)}
          alGuardar={guardar}
          alBorrar={() => {
            borrar(partida.id)
            reemplazar({ nombre: 'libreta' })
          }}
          alRevancha={() => {
            const nueva = crear({
              companero: partida.companero,
              rivales: partida.rivales,
              objetivo: partida.objetivo,
            })
            reemplazar({ nombre: 'libreta-partida', id: nueva.id })
          }}
        />
      ) : (
        <Vacio titulo="Esa partida no está en esta libreta">
          <p>Puede que se haya borrado, o que la anotaste en otro teléfono o con otra cuenta.</p>
        </Vacio>
      )}
    </div>
  )
}

function LibretaSuelta({
  partida,
  yo,
  alGuardar,
  alBorrar,
  alRevancha,
  alVolver,
}: {
  partida: PartidaPersonal
  alVolver: () => void
  yo: string
  alGuardar: (partida: PartidaPersonal) => boolean
  alBorrar: () => void
  alRevancha: () => void
}) {
  const cuenta = contar(partida)
  /* El dueño juega siempre en el bando 1, y el bando propio va a la izquierda. */
  const orden: [1 | 2, 1 | 2] = [1, 2]
  const nombre: Record<1 | 2, string> = { 1: 'Nosotros', 2: 'Ellos' }
  const quienes: Record<1 | 2, string> = {
    1: `${pila(yo)} y ${pila(partida.companero)}`,
    2: `${pila(partida.rivales[0])} y ${pila(partida.rivales[1])}`,
  }

  const [cifras, setCifras] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)
  const [corrigiendo, setCorrigiendo] = useState(false)
  const [mano, setMano] = useState<ManoAnotada | null>(null)

  const valor = Number(cifras)
  const listo = cifras !== '' && valor >= 1
  const concluida = cuenta.ganador !== null
  const numero = partida.manos.length + 1
  const ultima = partida.manos.length

  function escribir(manos: PartidaPersonal['manos']): boolean {
    const bien = alGuardar(conManos(partida, manos))
    setAviso(
      bien
        ? null
        : 'No se pudo guardar en este teléfono. Revisá que el navegador no esté en modo privado.',
    )
    return bien
  }

  function anotar(ganador: 1 | 2) {
    if (!listo || concluida) return
    escribir([...partida.manos, { bandoGanador: ganador, puntos: valor }])
    setCifras('')
  }

  function guardarCorreccion(ganador: 1 | 2) {
    if (!listo || !mano) return
    escribir(
      partida.manos.map((m, i) =>
        i + 1 === mano.numero ? { bandoGanador: ganador, puntos: valor } : m,
      ),
    )
    setMano(null)
    setCifras('')
  }

  function borrarMano() {
    if (!mano) return
    if (!window.confirm(`¿Borrar la mano M${mano.numero}?`)) return
    escribir(partida.manos.slice(0, -1))
    setMano(null)
    setCifras('')
  }

  function borrarPartida() {
    if (!window.confirm('¿Borrar esta partida de tu libreta? No se puede deshacer.')) return
    alBorrar()
  }

  function terminarCorreccion() {
    setCorrigiendo(false)
    setMano(null)
    setCifras('')
  }

  const estado = concluida
    ? cuenta.ganador === 1
      ? 'Ganamos. La partida ya cuenta en tu ranking.'
      : 'Ganaron ellos. La partida ya cuenta en tu ranking.'
    : `Se juega a ${partida.objetivo} puntos. Cuenta en el ranking cuando uno llega.`

  return (
    <div className={`libreta ${corrigiendo ? 'corrigiendo' : ''}`}>
      <CabezaLibreta textoVolver="Mi libreta" alVolver={alVolver}>
        <span className="libre-pastilla chica">Fuera de torneo</span> {cuandoFue(partida.creada)}
      </CabezaLibreta>

      <div className="libreta-marcador" aria-live="polite">
        <Lado partida={cuenta} bando={orden[0]} nombre={quienes[orden[0]]} />
        <div className="libreta-a">
          a<b>{partida.objetivo}</b>
        </div>
        <Lado partida={cuenta} bando={orden[1]} nombre={quienes[orden[1]]} derecha />
      </div>

      {corrigiendo ? (
        <>
          <AvisoCorreccion
            mano={mano}
            quienes={quienes}
            alSoltar={() => {
              setMano(null)
              setCifras('')
            }}
          />

          <Cuaderno
            partida={cuenta}
            orden={orden}
            quienes={quienes}
            elegida={mano?.numero ?? null}
            alElegir={(m) => {
              setMano(m)
              setCifras(String(m.puntos))
            }}
          />

          {mano ? (
            <Teclado
              titulo={`Corregir M${mano.numero}`}
              cifras={cifras}
              setCifras={setCifras}
              listo={listo}
              enviando={false}
              orden={orden}
              nombre={nombre}
              elegido={mano.bandoGanador}
              resultado={(b) => {
                const sin = puntosDe(b, cuenta) - (mano.bandoGanador === b ? mano.puntos : 0)
                return `${puntosDe(b, cuenta)} → ${sin + valor}`
              }}
              alElegir={guardarCorreccion}
            />
          ) : null}

          <div className="libreta-correccion-acciones">
            {mano && mano.numero === ultima && (
              <button className="libreta-borrar" onClick={borrarMano}>
                Borrar esta mano
              </button>
            )}
            <button className="boton principal" onClick={terminarCorreccion}>
              Listo
            </button>
          </div>
        </>
      ) : (
        <>
          {cuenta.manos.length > 0 ? (
            <div className="libreta-manos" aria-label="Manos anotadas, la última primero">
              {cuenta.manos
                .slice()
                .reverse()
                .map((m) => (
                  <span key={m.numero} className="libreta-mano">
                    <i className={m.bandoGanador === orden[0] ? 'nosotros' : 'ellos'} />
                    {`M${m.numero}: +${m.puntos}`}
                  </span>
                ))}
            </div>
          ) : (
            <p className="libreta-vacia">Todavía no anotaste ninguna mano.</p>
          )}

          {!concluida && (
            <Salida
              sillas={sillasDe(partida, yo)}
              salida={partida.salida}
              numero={numero}
              orden={orden}
              puede
              alAnotar={(salida) => {
                const bien = alGuardar({ ...partida, salida })
                setAviso(
                  bien
                    ? null
                    : 'No se pudo guardar en este teléfono. Revisá que el navegador no esté en modo privado.',
                )
              }}
            />
          )}

          {!concluida && (
            <Teclado
              titulo={`Mano ${numero}`}
              cifras={cifras}
              setCifras={setCifras}
              listo={listo}
              enviando={false}
              orden={orden}
              nombre={nombre}
              resultado={(b) => `${puntosDe(b, cuenta)} → ${puntosDe(b, cuenta) + valor}`}
              alElegir={anotar}
            />
          )}

          {concluida && (
            <button className="boton principal" onClick={alRevancha}>
              Revancha con los mismos
            </button>
          )}

          {partida.manos.length > 0 && (
            <button
              className="libreta-corregir"
              onClick={() => {
                setCorrigiendo(true)
                setMano(null)
                setCifras('')
              }}
            >
              Corregir
            </button>
          )}
        </>
      )}

      {aviso && (
        <p className="planilla-aviso" role="alert">
          {aviso}
        </p>
      )}

      <footer className="planilla-pie">
        <span>{estado}</span>
        <button className="libreta-borrar" onClick={borrarPartida}>
          Borrar partida
        </button>
      </footer>
    </div>
  )
}
